export type P3V1StatRecord = Record<string, unknown>;

export type P3V1PlayerStatMetric<Player> = {
  player: Player;
  stat: string;
  value: number;
};

const getNumericStatValue = (stats: P3V1StatRecord, stat: string) => {
  const value = stats[stat];
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (Array.isArray(value) && typeof value[0] === "number" && Number.isFinite(value[0])) {
    return value[0];
  }
};

export const getP3V1LeagueLeadingMetrics = <Player>(
  players: readonly Player[],
  stats: readonly string[],
  getStats: (player: Player) => P3V1StatRecord,
  limit = 3,
) =>
  stats
    .map((stat) => {
      let best: P3V1PlayerStatMetric<Player> | undefined;
      for (const player of players) {
        const value = getNumericStatValue(getStats(player), stat);
        if (value === undefined) {
          continue;
        }
        if (!best || value > best.value) {
          best = { player, stat, value };
        }
      }
      return best;
    })
    .filter((metric) => metric !== undefined)
    .slice(0, Math.max(0, limit));

export const getP3V1UserTeamStatHighlights = <Player>(
  players: readonly Player[],
  stats: readonly string[],
  userTid: number,
  getStats: (player: Player) => P3V1StatRecord,
  getTid: (player: Player) => number | undefined,
  limit = 3,
) =>
  getP3V1LeagueLeadingMetrics(
    players.filter((player) => getTid(player) === userTid),
    stats,
    getStats,
    limit,
  );

export const getP3V1PlayerStatsTrendContext = ({
  playoffs,
  season,
  statType,
}: {
  playoffs: string;
  season: number | "all" | "career";
  statType: string;
}) => ({
  mode: season === "all" ? "multiSeason" : season === "career" ? "career" : "season",
  playoffs,
  season,
  statType,
});

export type P3V1TeamStatLike = {
  seasonAttrs: P3V1StatRecord;
  stats: P3V1StatRecord;
  tid: number;
};

export type P3V1TeamMetricRank<Team extends P3V1TeamStatLike> = {
  rank: number;
  stat: string;
  team: Team;
  value: number;
};

export const getP3V1TeamMetricRanks = <Team extends P3V1TeamStatLike>(
  teams: readonly Team[],
  allStats: Record<string, readonly number[]>,
  stats: readonly string[],
  userTid: number,
) => {
  const userTeam = teams.find((team) => team.tid === userTid);
  if (!userTeam) {
    return [];
  }

  return stats
    .map((stat) => {
      const value = getNumericStatValue(userTeam.stats, stat);
      const seasonValue = getNumericStatValue(userTeam.seasonAttrs, stat);
      const statValue = value ?? seasonValue;
      const sortedValues = allStats[stat];
      if (statValue === undefined || !sortedValues) {
        return undefined;
      }

      const index = sortedValues.indexOf(statValue);
      if (index < 0) {
        return undefined;
      }

      // C4: the server's allStats arrays are sorted best-first (lowerIsBetter
      // ascending, otherwise descending — see server/worker/views/teamStats.ts),
      // so the league rank is the first index of the value plus one; indexOf
      // also makes tied values share the best rank.
      return {
        rank: index + 1,
        stat,
        team: userTeam,
        value: statValue,
      };
    })
    .filter((rank) => rank !== undefined);
};

export const getP3V1TeamStrengths = <Team extends P3V1TeamStatLike>(
  ranks: readonly P3V1TeamMetricRank<Team>[],
  limit = 3,
) => [...ranks].sort((a, b) => a.rank - b.rank).slice(0, Math.max(0, limit));

export const getP3V1TeamWeaknesses = <Team extends P3V1TeamStatLike>(
  ranks: readonly P3V1TeamMetricRank<Team>[],
  limit = 3,
  excludedStats: ReadonlySet<string> = new Set(),
) =>
  [...ranks]
    .filter((rank) => !excludedStats.has(rank.stat))
    .sort((a, b) => b.rank - a.rank)
    .slice(0, Math.max(0, limit));

// Standings order: win% first, then wins, then tid as a stable tiebreak —
// shared by the compact top-N ranking and the user-team anchor so both agree.
const compareTeamStanding = (a: P3V1TeamStatLike, b: P3V1TeamStatLike) => {
  const bWinp = getNumericStatValue(b.seasonAttrs, "winp") ?? 0;
  const aWinp = getNumericStatValue(a.seasonAttrs, "winp") ?? 0;
  const bWon = getNumericStatValue(b.seasonAttrs, "won") ?? 0;
  const aWon = getNumericStatValue(a.seasonAttrs, "won") ?? 0;
  return bWinp - aWinp || bWon - aWon || a.tid - b.tid;
};

export const getP3V1CompactTeamRanking = <Team extends P3V1TeamStatLike>(
  teams: readonly Team[],
  limit = 8,
) => [...teams].sort(compareTeamStanding).slice(0, Math.max(0, limit));

// R6-3: the compact ranking is sliced to the top 8, so a mid/low team never
// surfaces there. This resolves the user team's full-league standing (1-based
// rank by win%) plus its point differential (mov) for the first-screen anchor
// row, mirroring the playerRatings "best team player" / standings "your team"
// precedents. Returns undefined when the user team is absent (e.g. missed the
// playoffs in the playoffs-scoped view).
export const getP3V1UserTeamStanding = <Team extends P3V1TeamStatLike>(
  teams: readonly Team[],
  userTid: number,
) => {
  const ranked = [...teams].sort(compareTeamStanding);
  const index = ranked.findIndex((team) => team.tid === userTid);
  if (index < 0) {
    return undefined;
  }

  const team = ranked[index]!;
  return {
    mov: getNumericStatValue(team.stats, "mov"),
    rank: index + 1,
    team,
    teamCount: ranked.length,
  };
};
